import { Box, List, ListItem, ListItemText, TextField, Typography } from '@mui/material';
import type React from 'react';
import { useState } from 'react';
import { isValidGuid, sanitizeInput } from '../../utils/formUtils';

import WizardNavigation from './WizardNavigation';

interface Props {
  onNext: (data: { clientId: string }) => void;
  onBack: () => void;
  activeStep: number;
  credentials: {
    clientId: string;
  };
}

const Step3ClientId: React.FC<Props> = ({ onNext, onBack, activeStep, credentials }) => {
  const [clientId, setClientId] = useState(credentials.clientId || '');
  const [error, setError] = useState('');

  const handleClientIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitizedValue = sanitizeInput(e.target.value);
    setClientId(sanitizedValue);
    if (error) {
      validateClientId(sanitizedValue);
    }
  };

  const validateClientId = (value: string) => {
    if (!value) {
      setError('Client ID is required.');
      return false;
    } else if (!isValidGuid(value)) {
      setError('Please enter a valid Client ID (GUID format).');
      return false;
    } else {
      setError('');
      return true;
    }
  };

  const handleBlur = () => {
    validateClientId(clientId);
  };

  const handleNext = () => {
    if (validateClientId(clientId)) {
      onNext({ clientId });
    }
  };

  return (
    <Box sx={{ textAlign: 'left' }}>
      <Typography variant="h6" gutterBottom>
        Step 3: Create the BIDLOGIQ ‘app’ to get your Client ID
      </Typography>
      <Typography variant="body1" paragraph>
        You need to register BIDLOGIQ as an application in Azure so it has its own identity and
        credentials.
      </Typography>
      <List dense>
        <ListItem>
          <ListItemText
            primary={
              <span>
                <b>Register a New App:</b> In Microsoft Entra ID, select Manage then "App
                registrations" from the left menu, then click "+ New registration".
              </span>
            }
          />
        </ListItem>
        <ListItem sx={{ pl: 2 }}>
          <ListItemText primary='– Name: Give your app a clear name (e.g., "BIDLOGIQ App").' />
        </ListItem>
        <ListItem sx={{ pl: 2 }}>
          <ListItemText primary='– Supported account types: Leave the default option ("Accounts in this organizational directory only").' />
        </ListItem>
        <ListItem sx={{ pl: 2 }}>
          <ListItemText
            primary={
              <span>
                – Redirect URI: Leave this <b>blank</b> (we don't need it for server-to-server
                authentication).
              </span>
            }
          />
        </ListItem>
        <ListItem sx={{ pl: 2 }}>
          <ListItemText primary="– Click Register." />
        </ListItem>
        <ListItem>
          <ListItemText
            primary={
              <span>
                <b>Copy the Client ID:</b> On the “Overview” page of your new app, copy the
                "Application (client) ID". This is your Client ID.
              </span>
            }
          />
        </ListItem>
      </List>
      <TextField
        label="Client ID"
        value={clientId}
        onChange={handleClientIdChange}
        onBlur={handleBlur}
        fullWidth
        required
        variant="outlined"
        margin="normal"
        sx={{ mt: 2 }}
        error={!!error}
        helperText={error || 'Example: 12345678-abcd-1234-abcd-1234567890ab'}
      />
      <WizardNavigation
        activeStep={activeStep}
        onBack={onBack}
        onNext={handleNext}
        isNextDisabled={!clientId || !!error}
      />
    </Box>
  );
};

export default Step3ClientId;
